import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import AnalyticsFilterBar from './AnalyticsFilterBar';
import { toQuery, useAnalyticsFilters } from './filters';
import { DayTimeline, STATE_COLORS, TimeChart, TimelineSegment } from './charts';
import { fmtMin, fmtNum, fmtPct } from './format';
import { Kpi, Tile, alert, card, cardHead, grid, muted, td, tdR, th, thR } from './Overview';

// "Zlecenia" (spec 7.10): list of orders (assignment items) in the period and a card of one
// order — plan vs done, time on the line, speed and timeline inside the order's own window.

interface OrderRow extends Kpi { pivot_id: number; item_id: number; line_id: number; line: string; week: string | null; sku: string | null; name: string | null; material: string | null; color: string | null; brand: string | null; planned: number; done: number | null; status: string | null; started_at: string; completed_at: string | null; totalMin: number }
interface Detail {
  order: OrderRow & { notes: string | null; window_from: string; window_to: string };
  kpi: Kpi; lineKpi: Kpi;
  telemetry: { bucket_seconds: number; points: { t: string; speed: number | null; dx: number | null; dy: number | null; target: number | null; nominal: number }[] };
  timeline: (Omit<TimelineSegment, 'title'> & { item_id: number | null; sku: string | null; material: string | null; color: string | null; speed: number | null })[];
}

const ts = (iso: string) => Math.floor(new Date(iso).getTime() / 1000);
const dt = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const hhmm = (t: number) => { const d = new Date(t * 1000); return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; };
const ddmm = (t: number) => { const d = new Date(t * 1000); return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}`; };

const STATUS_COLORS: Record<string, string> = { completed: '#059669', in_progress: '#2f80ed', pending: '#94a3b8', niepelna_produkcja: '#d97706' };

const Orders: React.FC = () => {
  const { t } = useLanguage();
  const [filters, setFilters] = useAnalyticsFilters();
  const [orders, setOrders] = useState<OrderRow[] | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const { compare, ...rest } = toQuery(filters);
    api.get('/analytics/orders', { params: rest }).then(r => { setOrders(r.data.orders); setError(null); }).catch(err => setError(err?.response?.data?.message ?? t('analytics.loadError')));
  }, [filters, t]);

  useEffect(() => {
    if (selected === null) { setDetail(null); return; }
    api.get(`/analytics/orders/${selected}`).then(r => setDetail(r.data)).catch(err => setError(err?.response?.data?.message ?? t('analytics.loadError')));
  }, [selected, t]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (orders ?? []).filter(o => !q || [o.sku, o.name, o.material, o.color, o.brand, o.line].some(v => (v ?? '').toLowerCase().includes(q)));
  }, [orders, search]);

  const days = useMemo(() => {
    if (!detail) return [];
    const from = new Date(detail.order.window_from); from.setHours(0, 0, 0, 0);
    const to = new Date(detail.order.window_to);
    const out: { label: string; dayStart: number; segments: TimelineSegment[] }[] = [];
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      const dayStart = Math.floor(d.getTime() / 1000);
      const dayEnd = dayStart + 86400;
      out.push({
        label: ddmm(dayStart), dayStart,
        segments: detail.timeline.filter(s => s.end_ts > dayStart && s.start_ts < dayEnd).map(s => ({
          ...s, start_ts: Math.max(s.start_ts, dayStart), end_ts: Math.min(s.end_ts, dayEnd),
          // Other items on the same line are greyed out — the card is about this order.
          state: s.item_id !== null && s.item_id !== detail.order.item_id && s.state === 'prod' ? 'nodata' : s.state,
          title: `${hhmm(Math.max(s.start_ts, dayStart))}–${hhmm(Math.min(s.end_ts, dayEnd))} · ${t(`analytics.state_${s.state}`)}${s.sku ? ` · ${s.sku}` : ''}${s.speed ? ` · ${fmtNum(s.speed, 1)} m/min` : ''}`,
        })),
      });
    }
    return out;
  }, [detail, t]);

  const k = detail?.kpi;
  const pts = detail?.telemetry.points ?? [];
  const winFrom = detail ? ts(detail.order.window_from) : 0;
  const winTo = detail ? ts(detail.order.window_to) : 1;

  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#1e293b' }}>📋 {t('orders.title')}</h1>
        <p style={{ margin: '4px 0 0 0', color: '#64748b', fontSize: 14 }}>{t('orders.subtitle')}</p>
      </div>
      <AnalyticsFilterBar filters={filters} onChange={setFilters} showCompare={false} />
      {error && <div style={alert}>{error}</div>}

      {detail && k && (
        <div style={{ ...card, borderColor: '#667eea' }}>
          <div style={cardHead}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{detail.order.sku ?? '—'} <span style={{ fontWeight: 500, color: '#475569' }}>{detail.order.name}</span></div>
              <div style={muted}>{detail.order.line} · {detail.order.week} · {detail.order.material} {detail.order.color} · {detail.order.brand} · <span style={{ color: STATUS_COLORS[detail.order.status ?? ''] ?? '#475569', fontWeight: 700 }}>{detail.order.status}</span></div>
            </div>
            <button onClick={() => setSelected(null)} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>✕ {t('orders.close')}</button>
          </div>
          <div style={grid}>
            <Tile label={t('orders.planVsDone')} value={`${detail.order.done ?? k.pcs} / ${detail.order.planned || '—'}`} sub={detail.order.planned ? fmtPct((detail.order.done ?? k.pcs) / detail.order.planned, 0) : undefined} delta={null} vsLabel="" bar={detail.order.planned ? (detail.order.done ?? k.pcs) / detail.order.planned : null} />
            <Tile label={t('orders.totalTime')} value={fmtMin(detail.order.totalMin)} sub={`${dt(detail.order.started_at)} → ${detail.order.completed_at ? dt(detail.order.completed_at) : t('orders.open')}`} delta={null} vsLabel="" />
            <Tile label={t('analytics.colProd')} value={fmtMin(k.prodMin)} sub={t('orders.inWindow')} delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiDowntime')} value={fmtMin(k.downMin)} sub={`${k.downCnt}× · ${t('analytics.kpiShortStops').toLowerCase()} ${k.ssCnt}`} delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiAvgSpeed')} value={k.avgSpeed === null ? '—' : `${fmtNum(k.avgSpeed, 1)} m/min`} sub={k.tgtSpeed ? t('analytics.target', { value: fmtNum(k.tgtSpeed, 0) }) : undefined} delta={null} vsLabel="" />
            <Tile label="Performance" value={fmtPct(k.performance)} delta={null} vsLabel="" bar={k.performance} />
            <Tile label={t('analytics.kpiFlowKg')} value={`${fmtNum(k.kgFlow, 1)} kg`} sub={t('analytics.kpiFlowKgSub', { count: fmtNum(k.cycles) })} delta={null} vsLabel="" />
            <Tile label={t('analytics.kpiNetKg')} value={`${fmtNum(k.kgNet, 1)} kg`} sub={`${k.pcs} ${t('analytics.pcs')}`} delta={null} vsLabel="" />
          </div>
          {pts.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t('lineHistory.speedChart')} <span style={muted}>{t('orders.windowNote', { from: dt(detail.order.window_from), to: dt(detail.order.window_to) })}</span></div>
              <TimeChart from={winFrom} to={winTo} tickLabel={winTo - winFrom > 86400 * 1.5 ? ddmm : hhmm} format={v => fmtNum(v, 0)} min={0} height={200}
                series={[
                  { name: t('lineHistory.speedActual'), color: '#2f80ed', points: pts.map(p => ({ t: ts(p.t), v: p.speed })) },
                  { name: 'Target', color: '#e8590c', dashed: true, points: pts.map(p => ({ t: ts(p.t), v: p.target })) },
                ]} />
            </div>
          )}
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{t('lineHistory.timeline')} <span style={muted}>{t('orders.timelineNote')}</span></div>
          <DayTimeline days={days} shortStopSeconds={360} />
          <div style={{ display: 'flex', gap: 14, fontSize: 12, color: '#475569', marginTop: 8 }}>
            {[['prod', t('analytics.state_prod')], ['down_short', t('analytics.stShort')], ['down', t('analytics.stDown')], ['nodata', t('orders.otherOrNoData')]].map(([k2, label]) => (
              <span key={k2} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: STATE_COLORS[k2] }} />{label}</span>
            ))}
          </div>
        </div>
      )}

      <div style={card}>
        <div style={{ ...cardHead, alignItems: 'center' }}>
          <b>{t('orders.list')} <span style={muted}>({visible.length})</span></b>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder={t('orders.search')} style={{ padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, minWidth: 240 }} />
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={th}>{t('analytics.colLine')}</th><th style={th}>SKU</th><th style={th}>{t('lineHistory.colItem')}</th><th style={th}>{t('standards.colMaterial')}</th>
                <th style={thR}>{t('lineHistory.colPlan')}</th><th style={thR}>{t('lineHistory.colDoneTotal')}</th><th style={thR}>{t('lineHistory.colPcsInPeriod')}</th>
                <th style={thR}>{t('analytics.colProd')}</th><th style={thR}>{t('analytics.colDown')}</th><th style={thR}>{t('analytics.colSpeed')}</th><th style={thR}>Perf.</th>
                <th style={th}>{t('lineHistory.colStarted')}</th><th style={th}>{t('orders.colCompleted')}</th><th style={th}>{t('lineHistory.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {!orders ? <tr><td colSpan={14} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('common.loading')}</td></tr>
                : visible.length === 0 ? <tr><td colSpan={14} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 24 }}>{t('orders.empty')}</td></tr>
                : visible.map(o => (
                  <tr key={o.pivot_id} onClick={() => setSelected(o.pivot_id)} style={{ cursor: 'pointer', background: selected === o.pivot_id ? '#eef2ff' : undefined }}>
                    <td style={{ ...td, fontWeight: 700 }}>{o.line}</td>
                    <td style={{ ...td, fontWeight: 700 }}>{o.sku ?? '—'}</td>
                    <td style={{ ...td, whiteSpace: 'normal', minWidth: 220 }}>{o.name ?? '—'}<div style={muted}>{o.brand}{o.color ? ` · ${o.color}` : ''}{o.week ? ` · ${o.week}` : ''}</div></td>
                    <td style={td}>{o.material ?? '—'}</td>
                    <td style={tdR}>{o.planned || '—'}</td>
                    <td style={tdR}>{o.done ?? '—'}</td>
                    <td style={{ ...tdR, fontWeight: 700 }}>{o.pcs}</td>
                    <td style={tdR}>{o.hasTelemetry ? fmtMin(o.prodMin) : '—'}</td>
                    <td style={tdR}>{o.hasTelemetry ? fmtMin(o.downMin) : '—'}</td>
                    <td style={tdR}>{fmtNum(o.avgSpeed, 1)}</td>
                    <td style={tdR}>{fmtPct(o.performance)}</td>
                    <td style={td}>{dt(o.started_at)}</td>
                    <td style={td}>{dt(o.completed_at)}</td>
                    <td style={{ ...td, color: STATUS_COLORS[o.status ?? ''] ?? '#475569', fontWeight: 600 }}>{o.status ?? '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Orders;
